import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, tap, throwError, switchMap } from 'rxjs';
import { User } from '@/models/user';
import { Permission } from '@/models/Permission';
import { Module } from '@/models/moduleTypes';
import { jwtDecode } from 'jwt-decode';
import { Role } from '@/models/role';
import { API } from '@/models/api';
import { BaseResponse } from '@/models/baseResponse';
import { AuthResponse } from '@/models/authResponse';
import { ActivateRegistrationPayload } from '@/models/activateRegistration';
import { AssistTablesService } from '@/services/assist.service';
import { ASSIST_PRELOAD_CODES } from '@/models/assist-table-codes';
import { AdminService } from './admin.service';
import { DashboardDataService } from 'src/app/user/dashboard/dashboard/services/dashboard-data.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private assistService = inject(AssistTablesService);
  private adminService = inject(AdminService);
  private dashboardDataService = inject(DashboardDataService);

  private _accessToken = signal('');
  readonly accessToken = computed(() => this._accessToken());

  private _user = signal<User | null>(null);
  readonly user = computed(() => this._user());

  readonly userName = computed(() => `${this._user()?.firstname} ${this._user()?.lastname}`);

  readonly isLoggedIn = computed(() => !!this._user());

  login(username: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(API.Auth.Login.url, { username, password }).pipe(
      tap((res) => {
        this.dashboardDataService.clearCache();
        this._accessToken.set(res.data.accessToken);
      }),
    );
  }

  mfa(mfaCode: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(API.Auth.Mfa.url, { mfaCode }).pipe(
      tap((res) => {
        this._accessToken.set(res.data.accessToken);
        const user = this.tokenToUser(res.data.accessToken);
        this.setUser(user);
      }),
      switchMap((res) =>
        this.assistService.getAssistValuesByTableCodes(ASSIST_PRELOAD_CODES).pipe(map(() => res)),
      ),
      switchMap((res) => this.adminService.getUserSettings().pipe(map(() => res))),
      map((res) => res),
    );
  }

  sendMfaCode(email: string, mfaType: string): Observable<BaseResponse> {
    return this.http.post<BaseResponse>(API.Auth.SendMfa.url, { email, mfaType });
  }

  setTenantToJwt(tenantId: string): Observable<BaseResponse> {
    this.adminService.clearUserSettingsCache();
    this.adminService.clearUserProfilesCache();
    this.dashboardDataService.clearCache();

    return this.http.post<AuthResponse>(API.Auth.SetTenantToJwt.url, JSON.stringify(tenantId)).pipe(
      tap((res) => {
        this._accessToken.set(res.data.accessToken);
        const user = this.tokenToUser(res.data.accessToken);
        this.setUser(user);
      }),
      switchMap((res) =>
        this.assistService.getAssistValuesByTableCodes(ASSIST_PRELOAD_CODES).pipe(map(() => res)),
      ),
      switchMap((res) => this.adminService.getUserSettings().pipe(map(() => res))),
      map((res) => res),
    );
  }

  refresh(): Observable<boolean> {
    return this.http.get<BaseResponse>(API.Auth.Refresh.url).pipe(
      tap((res) => {
        this._accessToken.set(res.data.accessToken);
        const user = this.tokenToUser(res.data.accessToken);
        this.setUser(user);
      }),
      switchMap((res) =>
        this.assistService.getAssistValuesByTableCodes(ASSIST_PRELOAD_CODES).pipe(map(() => res)),
      ),
      switchMap((res) => this.adminService.getUserSettings().pipe(map(() => res))),
      map((res) => res.success),
      catchError((error) => {
        console.warn('Auth error:', error);
        this.setUser(null);
        return of(false);
      }),
    );
  }

  logout(): Observable<BaseResponse> {
    return this.http.post<BaseResponse>(API.Auth.Logout.url, {}).pipe(
      tap(() => {
        this.dashboardDataService.clearCache();
        this.adminService.clearUserProfilesCache();
        this.adminService.clearUserSettingsCache();
        this._accessToken.set('');
        this.setUser(null);
      }),
    );
  }

  forgotPassword(email: string) {
    return this.http.post<BaseResponse>(API.Auth.ForgotPassword.url, { email }).pipe(
      catchError((error) => {
        console.error('Reset password failed', error);
        return throwError(() => error);
      }),
    );
  }

  getResetPasswordData(id: string, token: string, mode: string) {
    // const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http
      .post<BaseResponse>(API.Auth.GetResetPasswordData.url, { id, token, mode })
      .pipe(
        map((res) => {
          if (!res.success) return null;
          return res.data;
        }),
      );
  }

  resetPassword(payload: ActivateRegistrationPayload): Observable<BaseResponse> {
    return this.http.post<BaseResponse>(API.Auth.ResetPassword.url, payload, {
      headers: { 'Content-Type': 'application/json', Bearer: payload.token },
    });
  }

  setUser(user: User | null) {
    this._user.set(user);
  }

  hasRoles(requiredRoles: Role[], matchAll: boolean = false): boolean {
    if (matchAll) {
      return requiredRoles.every((role: Role) => this._user()?.roles?.includes(role));
    } else {
      return requiredRoles.some((role: Role) => this._user()?.roles?.includes(role));
    }
  }

  hasModules(modules: Module[], matchAll: boolean = false): boolean {
    if (matchAll) {
      return modules.every((module: Module) => this._user()?.modules?.includes(module));
    } else {
      return modules.some((module: Module) => this._user()?.modules?.includes(module));
    }
  }

  hasPermissions(permission: Permission[], matchAll: boolean = false): boolean {
    if (this._user()?.roles?.includes(Role.SYSTEMADMIN)) {
      return true;
    }

    const userPermissions = this._user()?.permissions ?? [];
    const result = matchAll
      ? permission.every((required) => userPermissions.includes(required))
      : permission.some((required) => userPermissions.includes(required));

    return result;
  }

  private tokenToUser(token: string): User {
    const decoded = jwtDecode<JwtPayload>(token);

    const normalizeToArray = (value: string | string[] | undefined): string[] => {
      if (!value) return [];
      return Array.isArray(value) ? value : [value];
    };

    return {
      email: decoded.nameid,
      firstname: decoded.given_name,
      lastname: decoded.family_name,
      modules: normalizeToArray(decoded.modules) as Module[], // You can populate this if your token includes modules
      roles: normalizeToArray(decoded.role) as Role[], // consider to change to SET
      permissions: normalizeToArray(decoded.permissions) as Permission[], // consider to change to SET
      companies: normalizeToArray(decoded.companies) as string[],
      tenantId: decoded.tenant_id,
      tenantType: decoded.tenant_type,
    };
  }
}
